import {Injectable} from '@angular/core';
import * as Handlebars from 'handlebars';
import {forEach} from "lodash-es";
import {GoogleApiKey} from "../_core/services/google-api.service";

// import { LocationDataProcessingService } from './location-data-processing.service';

@Injectable({
  providedIn: 'root'
})
export class HandlebarsService {
  constructor() {
    Handlebars.registerHelper('formatSpecialties', function(specialtiesV2) {
      return JSON.stringify(specialtiesV2, null, 4); // Formats with newlines and indentation
    });

    Handlebars.registerHelper('addressLineJoin', function (array: string[], separator: string = ', ') {
      if (!Array.isArray(array)) return '';
      return array.join(separator);
    });

    // to check equality
    Handlebars.registerHelper('eq', function(a, b) {
      return a === b;
    });

    // to check equality (alternative naming)
    Handlebars.registerHelper('isEqual', function(a, b) {
      return a === b;
    });

    // logical helpers
    Handlebars.registerHelper('and', function (...args) {
      const options = args.pop();
      return args.every(Boolean);
    });

    Handlebars.registerHelper('or', function (...args) {
      const options = args.pop();
      return args.some(Boolean);
    });

    Handlebars.registerHelper('not', function (a) {
      return !a;
    });

    // Capitalize each word in a string, including those after commas or with non-breaking spaces
    Handlebars.registerHelper('capitalizeEachWord', function (text: any) {
      if (text == null) return '';
      const str = String(text); // Safely convert to string
      // Capitalize any letter that follows a separator (space, comma, non-breaking space) or is the start of the string
      return str
        .toLowerCase()
        .replace(/(^|[\s\u00A0,]+)([a-z])/g, (match, separator, letter) => {
          return separator + letter.toUpperCase();
        });
    });

    Handlebars.registerHelper('formatTime', function (time: string) {
      if (!time) return '--:--';
      const parts = time.split(':');
      if (parts.length < 2) return '--:--';
      const [h, m] = parts;
      let hour = parseInt(h, 10);
      if (isNaN(hour)) return '--:--';
      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12 || 12;
      return `${hour}:${m} ${ampm}`;
    });


// Format phone/fax number
    Handlebars.registerHelper('getFormattedPhoneFax', function (value: string) {
      if(value) {
        const regex = /\(?(\d{3})?\)?[-.\s]?(\d{3})?[-.\s]?(\d{4})?/;
        const match = value.match(regex);
        if (match) {
          return `(${match[1] || 'XXX'}) ${match[2] || 'XXX'}-${match[3] || 'XXXX'}`;
        }
        return value;
      } else {
        return '';
      }
    });

// Format URL by removing http/https
    Handlebars.registerHelper('formatUrl', function (url: string) {
      return url ? url.replace(/^(https?:\/\/)/, '') : '';
    });

    // getting unique specialties
    Handlebars.registerHelper('getUniqueSpecialties', function (specialties: any[]) {
      if (!Array.isArray(specialties)) return [];

      const displayNames = specialties.map(s => s.display.toLowerCase());
      const uniqueNames = [...new Set(displayNames)];

      // Return the array so it can be used with #each
      return uniqueNames;
    });




    Handlebars.registerHelper('joinLines', function (lines: string[] = [], separator = ', ') {
      if (!Array.isArray(lines)) return '';
      return lines.join(separator);
    });

    // Join array of strings with comma separator
    Handlebars.registerHelper('joinWithComma', function (array: string[]) {
      if (!Array.isArray(array)) return '';
      return array.join(', ');
    });


    function transformTime(time: string | null | undefined): string | null {
      if (time === null || time === undefined) {
        return null;
      }

      const [hours, minutes, seconds] = time.split(':').map((val) => parseInt(val));

      if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
        return null;
      }

      // Convert to 12-hour format
      const hours12 = hours % 12 || 12; // This handles the 12-hour format conversion
      const amPm = hours < 12 ? 'AM' : 'PM';

      // Format minutes with two digits using padStart
      const formattedMinutes = minutes.toString().padStart(2, '0');

      // Return formatted time with no leading zero for hours
      return `${hours12}:${formattedMinutes} ${amPm}`;
    }


    Handlebars.registerHelper('getFormattedAvailableTime', function (data: any) {
      if (!data?.location?.availableTime || !Array.isArray(data.location.availableTime)) return [];

      const transformTime = (timeStr: string) => {
        if (!timeStr) return '--:--';
        const [h, m] = timeStr.split(':');
        let hour = parseInt(h, 10);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        hour = hour % 12 || 12;
        return `${hour}:${m} ${ampm}`;
      };

      return data.location.availableTime.map((entry: any) => {
        let time = '--:-- - --:--';

        if (entry.allDay) {
          time = '12:00 AM - 12:00 AM';
        } else if (entry.availableStartTime && entry.availableEndTime) {
          time = `${transformTime(entry.availableStartTime)} - ${transformTime(entry.availableEndTime)}`;
        }

        return {
          DayValue: entry.displayDay,
          DayCode: entry.daysOfWeek,
          TimeValues: time
        };
      });
    });




    Handlebars.registerHelper('formatCrisisHours', function (availableTime: any[]) {
      const dayMapping = {
        MON: 'Monday',
        TUE: 'Tuesday',
        WED: 'Wednesday',
        THU: 'Thursday',
        FRI: 'Friday',
        SAT: 'Saturday',
        SUN: 'Sunday',
      } as const;

      const days = Object.keys(dayMapping) as Array<keyof typeof dayMapping>;

      const transformTimeLocal = (timeStr: string | null | undefined): string => {
        if (!timeStr) return '--:--';
        const parts = timeStr.split(':');
        if (parts.length < 2) return '--:--';
        const [h, m] = parts;
        let hour = parseInt(h, 10);
        if (isNaN(hour)) return '--:--';
        const ampm = hour >= 12 ? 'PM' : 'AM';
        hour = hour % 12 || 12;
        return `${hour}:${m} ${ampm}`;
      };

      return days.map((day) => {
        const longFormDay = dayMapping[day];
        const dataForDay = Array.isArray(availableTime) ? availableTime.find(
          (item) => item.daysOfWeek === day
        ) : null;

        let time = '--:-- - --:--';
        if (dataForDay?.allDay === true) {
          time = '12:00 AM - 12:00 AM';
        } else if (dataForDay?.allDay === false && (dataForDay?.availableStartTime || dataForDay?.availableEndTime)) {
          time = `${transformTimeLocal(dataForDay.availableStartTime)} - ${transformTimeLocal(dataForDay.availableEndTime)}`;
        }

        return {
          DayValue: longFormDay,
          DayCode: day,
          TimeValues: time,
        };
      });
    });

    Handlebars.registerHelper('getUniqueCommunication', function (communication) {
      const res = new Set<string>();
      communication?.forEach((item: any) => {
        if (item.display) res.add(item.display);
      });
      return Array.from(res);
    });


    Handlebars.registerHelper('or', function (...args: any[]) {
      const options = args.pop(); // last argument is handlebars options
      return args.some(Boolean);
    });

    Handlebars.registerHelper('getAllInsuranceCarriers', function (root) {
      const result: string[] = [];
      const seen = new Set<string>();

      const network = root?.network;
      if (!Array.isArray(network)) return [];

      forEach(network, (entry) => {
        if (entry?.insurance?.[0] && !entry.meta?.['suppress']) {
          const planName = entry.insurance[0].plan?.name;
          if (planName && !seen.has(planName)) {
            seen.add(planName);
            result.push(planName);
          }
        }
      });

      return result;
    });

    Handlebars.registerHelper('extractNetworkNames', function(network) {
      if (!Array.isArray(network)) return [];
      // Extract names and filter out non-strings/empties
      const names = network.map(item => item?.name?.value).filter(Boolean);
      // Remove duplicates by converting to a Set and back to array
      const uniqueNames = [...new Set(names)];
      return uniqueNames;
    });


    Handlebars.registerHelper('getStaticMapSrc', function (root) {
      const pos = root?.location?.position?.[0];
      if (!pos) return '';
      const center = `${pos.lat},${pos.lon}`;
      const size = `350x350`;
      const markers = `label:A|scale:2|${center}`;
      return `https://maps.googleapis.com/maps/api/staticmap?center=${center}&zoom=14&size=${size}&markers=${markers}&key=${GoogleApiKey}`;
    });

    Handlebars.registerHelper('getMapDirectionsUrl', function (root, locationText) {
      const pos = root?.location?.position?.[0];
      if (!pos) return '';

      // Use the address text if available, otherwise fallback to lat/lon coordinates
      const queryValue = root?.location?.address?.[0]?.text || `${pos.lat},${pos.lon}`;
      const destination = encodeURIComponent(queryValue);

      // This URL opens Google Maps and drops a pin on the specific location
      return `https://www.google.com/maps/search/?api=1&query=${destination}`;
    });

    // provider/facility info tab table helpers
    // Handlebars.registerHelper('newlineList', function (array: string[]) {
    //   if (!Array.isArray(array)) return '';
    //   return array.map((item, index) => {
    //     const isLast = index === array.length - 1;
    //     return item + (isLast ? '.' : ',');
    //   });
    // });
    Handlebars.registerHelper('newlineList', function (list) {
      if (!Array.isArray(list)) return '';
      return new Handlebars.SafeString(list.join('<br>'));
    });

    const validAcceptingPatientCodes = ['newpt', 'newchpt', 'newmcdpt'];

    const asArray = <T = any>(value: any): T[] => {
      if (Array.isArray(value)) {
        return value.filter((item) => item !== null && item !== undefined);
      }
      return value !== null && value !== undefined ? [value] : [];
    };

    const getFirstLocation = (relationOrRoot: any): any => {
      const location = relationOrRoot?.location;
      if (Array.isArray(location)) {
        return location[0];
      }
      return location;
    };

    const getRelationsFromContext = (context: any): any[] => {
      if (Array.isArray(context?.relations)) {
        return context.relations;
      }

      // relation-level context (show-more tab)
      if (context && (context.location || context.acceptingPatients || context.accessibility)) {
        return [context];
      }

      return [];
    };

    const getAcceptingPatientsDisplay = (value: any): string | null => {
      if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed ? trimmed : null;
      }

      const candidates = asArray(value);
      const matched = candidates.find((item: any) =>
        validAcceptingPatientCodes.includes(item?.code)
      );

      return matched?.display || null;
    };

    const normalizeAccessibility = (value: any): Array<{ display: string }> => {
      return asArray(value)
        .map((item: any) => item?.display)
        .filter((display: any) => typeof display === 'string' && display.trim() !== '')
        .map((display: string) => ({ display }));
    };

    const resolveAdditionalAcceptingPatients = (acceptingPatients: any, context: any): string | null => {
      const direct = getAcceptingPatientsDisplay(acceptingPatients);
      if (direct) return direct;

      const contextLevel = getAcceptingPatientsDisplay(context?.acceptingPatients);
      if (contextLevel) return contextLevel;

      const contextLocationLevel = getAcceptingPatientsDisplay(getFirstLocation(context)?.acceptingPatients);
      if (contextLocationLevel) return contextLocationLevel;

      const relations = getRelationsFromContext(context);
      for (const relation of relations) {
        const relationLevel = getAcceptingPatientsDisplay(relation?.acceptingPatients);
        if (relationLevel) return relationLevel;

        const locationLevel = getAcceptingPatientsDisplay(getFirstLocation(relation)?.acceptingPatients);
        if (locationLevel) return locationLevel;
      }

      return null;
    };

    const resolveAdditionalAccessibility = (accessibility: any, context: any): Array<{ display: string }> => {
      const direct = normalizeAccessibility(accessibility);
      if (direct.length > 0) return direct;

      const contextLevel = normalizeAccessibility(context?.accessibility);
      if (contextLevel.length > 0) return contextLevel;

      const contextLocationLevel = normalizeAccessibility(getFirstLocation(context)?.accessibility);
      if (contextLocationLevel.length > 0) return contextLocationLevel;

      const relations = getRelationsFromContext(context);
      for (const relation of relations) {
        const relationLevel = normalizeAccessibility(relation?.accessibility);
        if (relationLevel.length > 0) return relationLevel;

        const locationLevel = normalizeAccessibility(getFirstLocation(relation)?.accessibility);
        if (locationLevel.length > 0) return locationLevel;
      }

      return [];
    };

    Handlebars.registerHelper('getResolvedAdditionalAcceptingPatients', function (this: any, acceptingPatients: any, context: any) {
      return resolveAdditionalAcceptingPatients(acceptingPatients, context || this);
    });

    Handlebars.registerHelper('getResolvedAdditionalAccessibility', function (this: any, accessibility: any, context: any) {
      return resolveAdditionalAccessibility(accessibility, context || this);
    });


    Handlebars.registerHelper('getNewPatientStatus', function (acceptingPatients: any[]) {
      const validCodes = ['newpt', 'newchpt', 'newmcdpt'];
      if (Array.isArray(acceptingPatients) && validCodes.includes(acceptingPatients[0]?.code)) {
        return acceptingPatients[0].display;
      }
      return null;
    });


    Handlebars.registerHelper('getAccessibility', function (accessibility: any[]) {
      if (!Array.isArray(accessibility)) return [];
      return accessibility.map(item => ({ display: item.display }));
    });

    Handlebars.registerHelper('getPrimaryCare', function (codes: any[]) {
      if (!Array.isArray(codes)) return [];
      return codes.filter(c => c.display === 'Primary Care Provider');
    });

    Handlebars.registerHelper('getSpecialist', function (codes: any[]) {
      if (!Array.isArray(codes)) return [];
      return codes.filter(c => c.display === 'Specialist');
    });

    Handlebars.registerHelper('getNetwork', function (providerIdentifier: any[]) {
      if (!Array.isArray(providerIdentifier)) return null;
      const prn = providerIdentifier.find((a) => a.type === "PRN");
      return prn ? prn.value : null;
    });

    Handlebars.registerHelper('or', function () {
      return Array.prototype.slice.call(arguments, 0, -1).some(Boolean);
    });

    Handlebars.registerHelper('notEmpty', function (arr) {
      return Array.isArray(arr) && arr.length > 0;
    });

    Handlebars.registerHelper('isSecondColumn', function (
      this: any,
      dayCode: string,
      options: Handlebars.HelperOptions
    ) {
      const secondColumnDays = ['FRI', 'SAT', 'SUN'];
      const isSecond = secondColumnDays.includes(dayCode);

      // If options.fn is defined, it's a block helper
      if (options && typeof options.fn === 'function') {
        return isSecond ? options.fn(this) : options.inverse(this);
      }

      // Otherwise, return boolean for use in sub-expressions like {{#if (isSecondColumn ...)}}
      return isSecond;
    });

    Handlebars.registerHelper('isWeekend', function (
      this: any,
      dayCode: string,
      options: Handlebars.HelperOptions
    ) {
      const weekendDayCodes = ['FRI', 'SAT', 'SUN'];
      const isWeekendDay = weekendDayCodes.includes(dayCode);

      // If options.fn is defined, it's a block helper
      if (options && typeof options.fn === 'function') {
        return isWeekendDay ? options.fn(this) : options.inverse(this);
      }

      // Otherwise, return boolean for use in sub-expressions like {{#if (isWeekend ...)}}
      return isWeekendDay;
    });

    // to increment value by 1 for the @index
    Handlebars.registerHelper('inc', function(value) {
      return parseInt(value) + 1;
    });

    Handlebars.registerHelper('isTelehealthEnabled', function (virtualServiceDetail: any[]) {
      return Array.isArray(virtualServiceDetail) &&
        virtualServiceDetail.some((v: any) =>
          Array.isArray(v.channelType) &&
          v.channelType.some((c: { display?: string }) => c.display === "Telehealth services")
        );
    });

    Handlebars.registerHelper('isTelehealthEnabledMDDOH', function(characteristics: any[]) {
      // Check if Telehealth Services is present in the characteristics array
      const telehealthService = characteristics.find(c => c.text === 'Telehealth Services');

      // If found, return "Telehealth Services", else return null or a default value
      return telehealthService ? 'Telehealth Services' : null;
    });

    Handlebars.registerHelper('isOnlyTelehealthCharacteristic', function (characteristics: any[]) {
      if (!Array.isArray(characteristics) || characteristics.length !== 1) {
        return false;
      }

      const onlyValue = characteristics[0];
      const textValue =
        typeof onlyValue === 'string'
          ? onlyValue
          : onlyValue?.text;

      if (typeof textValue !== 'string') {
        return false;
      }

      return textValue.trim().toLowerCase() === 'telehealth services';
    });

    // for the MDDOH plan drop down data convertion
    Handlebars.registerHelper('stringArray', function (array) {
      if (Array.isArray(array)) {
        return JSON.stringify(array);
      }
      return '[]';
    });

    // Returns true if input is an array (including empty array [] or with length > 0) For champion ProgramType Array
    Handlebars.registerHelper('checkProgramTypeArray', function (arr) {
      return Array.isArray(arr);
    });

    /**
     * Handlebars Helper: getFirstFacility
     *
     * Returns the first facility name from the facility array.
     * - Extracts `name.value` from each facility object.
     * - Filters out empty/undefined values.
     * - Returns only the 0th index (the first facility name).
     *
     * Used for displaying the primary (first) facility on its own line.
     */
    Handlebars.registerHelper("getFirstFacility", function (facilityArray) {
      if (!Array.isArray(facilityArray)) return "";

      const names = facilityArray
        .map(f => f?.name?.value)
        .filter(n => !!n);

      return names[0] || "";
    });


    /**
     * Handlebars Helper: getOtherFacilities
     *
     * Returns a comma-separated string of all *unique* facility names
     * excluding the first (0th index) item.
     * - Extracts `name.value`
     * - Filters empty values
     * - Removes duplicates using Set
     * - Drops the 0th element
     * - Joins the remaining elements with commas
     *
     * Used for showing additional facilities on a new line.
     */
    Handlebars.registerHelper("getOtherFacilities", function (facilityArray) {
      if (!Array.isArray(facilityArray)) return "";

      const names = facilityArray
        .map(f => f?.name?.value)
        .filter(n => !!n);

      const unique = [...new Set(names)];

      // Drop the first item (index 0)
      const others = unique.slice(1);

      return others.join(", ");
    });


    /**
     * Handlebars Helper: getOtherCount
     *
     * Returns the count of unique facilities excluding the first one.
     * - Extracts unique facility names
     * - If more than one exists, returns (length - 1)
     * - Otherwise returns 0
     *
     * Used for displaying "+N" count labels for additional facilities.
     */
    Handlebars.registerHelper("getOtherCount", function (facilityArray) {
      if (!Array.isArray(facilityArray)) return 0;

      const names = facilityArray
        .map(f => f?.name?.value)
        .filter(n => !!n);

      const unique = [...new Set(names)];

      return unique.length > 1 ? unique.length - 1 : 0;
    });

    // For capitalizing the first letter of each word in a string
    Handlebars.registerHelper('capitalizeText', function (text: any) {
      if (!text) return '';

      const format = (str: string) => {
        return str
          // Normalize extra whitespace only
          .replace(/\s+/g, ' ')
          .trim()

          // Title-case words but keep punctuation untouched
          .split(/([ -/()'])/)
          .map((part, index, array) => {
            if (/^[ -/()']$/.test(part)) {
              return part;
            }
            // Handle possessive 's or 'S
            if (index > 0 && array[index - 1] === "'" && part.toLowerCase() === 's') {
              return part.toLowerCase();
            }
            return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
          })
          .join('');
      };

      // Array input
      if (Array.isArray(text)) {
        const formattedArray = text
          .filter(item => typeof item === 'string' && item.trim() !== '')
          .map(item => format(item));
        return [...new Set(formattedArray)];
      }

      // String input
      if (typeof text === 'string') {
        return format(text);
      }

      return '';
    });





  }

  compileTemplate(source: string, context: any): string {
    const template = Handlebars.compile(source);

    return template(context);
  }

}
